import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { onboardingHref } from './lib/onboarding-model';
import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from './lib/auth';
import { AppQueryProvider } from './lib/query';
import { WorkspaceGate } from './lib/workspace';
import { AppShell } from './components/layout/AppShell';
import { SettingsLayout } from './components/layout/SettingsLayout';
import { RequireAuth } from './components/layout/RequireAuth';
import { Landing } from './pages/Landing';
import { SupplierAiRegisterSample } from './pages/samples/SupplierAiRegisterSample';
import { BuildingAiRegisterSample } from './pages/samples/BuildingAiRegisterSample';
import { SignIn, SignUp } from './pages/SignIn';
import { WorkspacePicker } from './pages/WorkspacePicker';
import { UnifiedOnboarding } from './pages/UnifiedOnboarding';
import { Setup } from './pages/Setup';
import { Billing } from './pages/Billing';
import { TeamSettings } from './pages/TeamSettings';
import { InviteAccept } from './pages/InviteAccept';
import { SsoSettings } from './pages/SsoSettings';
import { ComplianceSettings } from './pages/ComplianceSettings';
import { CoverageRegionSettings } from './pages/CoverageRegionSettings';
import { Dashboard } from './pages/Dashboard';
import { Register } from './pages/Register';
import { RegisterItemDetail } from './pages/RegisterItemDetail';
import { Suppliers } from './pages/Suppliers';
import { SupplierDetail } from './pages/SupplierDetail';
import { AttestedCapabilities } from './pages/AttestedCapabilities';
import { AuditSettings } from './pages/AuditSettings';
import { AuditEventDetail } from './pages/AuditEventDetail';
import { MyWork } from './pages/MyWork';
import { Roadmap } from './pages/Roadmap';
import { Systems } from './pages/Systems';
import { ComponentInventory } from './pages/ComponentInventory';
import { Reports } from './pages/Reports';
import { Evidence } from './pages/Evidence';
import { AuditLog } from './pages/AuditLog';
import { WebhookSettings } from './pages/WebhookSettings';
import { EventWebhookSettings } from './pages/EventWebhookSettings';
import { AssessorEngagements } from './pages/AssessorEngagements';
import { ApiTokenSettings } from './pages/ApiTokenSettings';
import { DisputesInbox } from './pages/DisputesInbox';
import { AssessorInbox } from './pages/AssessorInbox';
import { AssessorWorkpaper } from './pages/AssessorWorkpaper';
import { FirmWorkpaper } from './pages/FirmWorkpaper';
import { FirmClientView } from './pages/FirmClientView';
import { AuditFirmEngagements } from './pages/AuditFirmEngagements';
import { AuditFirmHome } from './pages/AuditFirmHome';
import { MyEvidenceRequests } from './pages/MyEvidenceRequests';
import { Queue } from './pages/Queue';
import { AuthorRule } from './pages/AuthorRule';
import { Sources } from './pages/Sources';
import { Questionnaires } from './pages/Questionnaires';
import { QuestionnaireNew } from './pages/QuestionnaireNew';
import { QuestionnaireDetail } from './pages/QuestionnaireDetail';
import { Editors } from './pages/Editors';
import { EditorDetail } from './pages/EditorDetail';
import { Research } from './pages/Research';
import { VerifyInfraDecision } from './pages/VerifyInfraDecision';
import { VerifyInfraPlan } from './pages/VerifyInfraPlan';
import { VerifyInsurabilityPack } from './pages/VerifyInsurabilityPack';
import { VerifyMonitoringPlan } from './pages/VerifyMonitoringPlan';
import { VerifyMonitoringStatus } from './pages/VerifyMonitoringStatus';
import { Monitoring } from './pages/Monitoring';
import { ConfigurationItemPage } from './pages/ConfigurationItemPage';
import { RegisterCitations } from './pages/RegisterCitations';
import { Security } from './pages/Security';
import { AlertsInbox } from './pages/AlertsInbox';
import { AlertDetail } from './pages/AlertDetail';
import { AlertPreferences } from './pages/AlertPreferences';
import { ProfileSettings } from './pages/ProfileSettings';
import { UseCaseIntake } from './pages/UseCaseIntake';
import { DiscoveredTools } from './pages/DiscoveredTools';
import { Exceptions } from './pages/Exceptions';
import { CalendarTrust } from './pages/CalendarTrust';
import { IncidentIntake } from './pages/IncidentIntake';
import { IncidentDetail } from './pages/IncidentDetail';
import { InsurabilityPack } from './pages/InsurabilityPack';
import { Findings } from './pages/Findings';
import { ReleaseManager } from './pages/ReleaseManager';
import { SupplierRegister } from './pages/SupplierRegister';
import { SignatureRoutingSettings } from './pages/SignatureRoutingSettings';
import { SigningSettings } from './pages/SigningSettings';
import { ControlSpecSettings } from './pages/ControlSpecSettings';
import { SystemDetail } from './pages/SystemDetail';
import { CommonControls } from './pages/CommonControls';
import { TestLibrary } from './pages/TestLibrary';
import { Blueprint } from './pages/Blueprint';
import { PlanStage } from './pages/stages/PlanStage';
import { BuildStage } from './pages/stages/BuildStage';
import { ReleaseStage } from './pages/stages/ReleaseStage';
import { OperateStage } from './pages/stages/OperateStage';
import { SellStage } from './pages/stages/SellStage';
import { BuyingLanding } from './pages/BuyingLanding';
import { BuildingLanding } from './pages/BuildingLanding';
import { Pricing } from './pages/Pricing';
import { Consultancy } from './pages/Consultancy';
import { QuestionnaireResponse } from './pages/QuestionnaireResponse';
import { OrgDashboard } from './pages/OrgDashboard';
import { OrgSettings } from './pages/OrgSettings';
import { OrgAiSystems } from './pages/OrgAiSystems';
import { OrgSupplierPortfolio } from './pages/OrgSupplierPortfolio';
import { OrgSupplierDetail } from './pages/OrgSupplierDetail';
import { OrgVendorAttestations } from './pages/OrgVendorAttestations';
import { OrgDisclosureRequests } from './pages/OrgDisclosureRequests';
import { OrgFindings } from './pages/OrgFindings';
import { OrgIncidents } from './pages/OrgIncidents';
import { OrgAlerts } from './pages/OrgAlerts';
import { OrgShell } from './components/layout/OrgShell';
import { ProgramsIndex } from './pages/ProgramsIndex';
import { PoliciesPage } from './pages/PoliciesPage';
import { AssessorWorkpaperIndex, BoundaryRedirect, WorkspaceRedirect } from './pages/NavRedirects';
import { ProgramShell } from './pages/ProgramShell';
import { CmmcPoam } from './pages/CmmcPoam';
import { ControlDetail } from './pages/ControlDetail';
import { MockAudit } from './pages/MockAudit';
import { TransitionPage } from './pages/programs/TransitionPage';
import { ReplacePage } from './pages/programs/ReplacePage';
import { ResponsePage } from './pages/ResponsePage';
import { Today } from './pages/Today';
import { ItemPage } from './pages/ItemPage';

/**
 * Auth-gated landing decision. Signed-in visitors bounce into their
 * default workspace's dashboard; onboarding-pending users to /onboarding;
 * unauthenticated visitors get the public marketing homepage.
 *
 * `default_workspace` now sources routing (came from /api/auth/me in
 * M1.5). `profile_id` is still consulted as a legacy fallback until
 * every user carries a workspace after the legacy-bootstrap migration
 * (007-legacy-workspace-bootstrap.sql).
 */
function AppRoot() {
  const { user, loading } = useAuth();
  // While /api/auth/me (and the profile read) resolve, a signed-in visitor must not see the public
  // marketing page: render the same blank spacer RequireAuth uses, then route once auth is final.
  if (loading) return <div style={{ padding: '2rem' }} aria-busy="true" />;
  if (user) {
    // Bridge: honour a stashed post-signin redirect (invite-accept
    // flow bounces through WorkOS and lands here or in
    // LegacyAppRedirect; whichever fires first consumes the stash).
    try {
      const stashed = window.sessionStorage.getItem(
        'aigovops:post-signin-redirect'
      );
      if (stashed) {
        window.sessionStorage.removeItem('aigovops:post-signin-redirect');
        return <Navigate to={stashed} replace />;
      }
    } catch {
      /* sessionStorage not available */
    }
  }
  if (user?.default_workspace?.slug) {
    return <Navigate to={`/w/${user.default_workspace.slug}/app/dashboard`} replace />;
  }
  // ADR-0015 post-signin routing:
  //   No default workspace → send to /workspaces. WorkspacePicker
  //   dispatches to /onboarding/start on 0 memberships, straight to
  //   the sole workspace's dashboard on 1, or renders the picker on
  //   >1 (case 5). Prior behaviour (Navigate to /onboarding/start
  //   always) skipped case 5.
  if (user) return <Navigate to="/workspaces" replace />;
  return <Landing />;
}

/** Wrap a page in the standard `/w/:slug/app/*` chrome stack. */
function NewSystemRedirect() {
  const { slug } = useParams();
  return <Navigate to={onboardingHref(slug ?? '', 'first_system')} replace />;
}

function workspacePage(node: ReactNode) {
  return (
    <RequireAuth>
      <WorkspaceGate>
        <AppShell>{node}</AppShell>
      </WorkspaceGate>
    </RequireAuth>
  );
}

/**
 * ADR-0010 · PR-P1-1: /w/:slug/app/reports → /w/:slug/app/deliverables
 * redirect. Kept for a 12-month bookmark bridge; see the route
 * definition below. Preserves the caller's query + hash so a bookmark
 * like `/reports?tab=exports#section-x` lands correctly on the new
 * URL. TODO(2027-08): delete this component + its route with the
 * 12-month sunset.
 */
function ReportsToDeliverablesRedirect() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  if (!slug) return <Navigate to="/" replace />;
  const target = `/w/${encodeURIComponent(slug)}/app/deliverables${location.search ?? ''}${location.hash ?? ''}`;
  return <Navigate to={target} replace />;
}


export function App() {
  return (
    <AuthProvider>
      <AppQueryProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<AppRoot />} />
          <Route path="/signin" element={<SignIn />} />
          {/* ADR-0015 · /signup is a synonym for /signin with the
              sign-up form pre-selected. WorkOS AuthKit surfaces both
              tabs; screen_hint=sign-up flips which is active. */}
          <Route path="/signup" element={<SignUp />} />
          {/* ADR-0015 · Post-signin picker for multi-workspace users
              with no default. Also handles 0-membership / 1-membership
              shortcuts. Gated by RequireAuth. */}
          <Route
            path="/workspaces"
            element={
              <RequireAuth>
                <WorkspacePicker />
              </RequireAuth>
            }
          />
          <Route path="/editors" element={<Editors />} />
          <Route path="/editors/:id" element={<EditorDetail />} />
          <Route path="/research" element={<Research />} />
          <Route path="/trust" element={<Navigate to="/" replace />} />
          {/* V105-5-5: public, readable verify page for a signed strategy decision (signed facts only). */}
          <Route path="/verify/infra-decision/:id" element={<VerifyInfraDecision />} />
          <Route path="/verify/infra-plan/:id" element={<VerifyInfraPlan />} />
          <Route path="/verify/insurability-pack/:id" element={<VerifyInsurabilityPack />} />
          <Route path="/verify/monitoring-plan/:id" element={<VerifyMonitoringPlan />} />
          {/* V113-7-4: the public monitoring status (live by link, or one immutable snapshot); signed fields only. */}
          <Route path="/verify/monitoring-status/snapshot/:id" element={<VerifyMonitoringStatus />} />
          <Route path="/verify/monitoring-status/:id" element={<VerifyMonitoringStatus />} />
          {/* GTM v4 sample surfaces — public, no auth, static seed data.
              See docs/v4/link-gaps-plan.md GAP-1 + GAP-2.
              V85-2-7: moved under /buying/ so buying-sealed.spec.ts holds —
              old /samples/* paths redirect below for backward compat. */}
          <Route path="/samples/supplier-ai-register" element={<Navigate to="/buying/samples/supplier-ai-register" replace />} />
          <Route path="/samples/building-ai-register" element={<Navigate to="/buying/samples/building-ai-register" replace />} />
          {/* V4-6-2: public marketing micro-sites */}
          <Route path="/buying" element={<BuyingLanding />} />
          <Route path="/buying/samples/supplier-ai-register" element={<SupplierAiRegisterSample />} />
          <Route path="/buying/samples/building-ai-register" element={<BuildingAiRegisterSample />} />
          <Route path="/buying/pricing" element={<Pricing />} />
          <Route path="/building" element={<BuildingLanding />} />
          <Route path="/building/questionnaire-response" element={<QuestionnaireResponse />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/consultancy" element={<Consultancy />} />
          {/* Post-signup fork — where the user picks Personal vs Team.
              Personal-email users skip through automatically; corporate
              email users see the two-option decision (M2.3). */}
          {/* PR-Cluster-6-k: Jordan's cross-workspace inbox.
              Signed-in-user only — the server's assessor session
              gate handles the actual role check. NOT
              workspace-scoped: the whole point is picking WHICH
              workspace to enter next. */}
          <Route path="/assessor" element={<AssessorInbox />} />
          {/* V98-6 / ADR-0047: a firm member's engagements, and the firm-only
              working papers — NOT under WorkspaceGate (firm members hold no
              client membership; the API's firm guard is the gate). */}
          <Route path="/audit-firm" element={<RequireAuth><AuditFirmHome /></RequireAuth>} />
          <Route path="/w/:slug/app/engagements/:engagementId/firm" element={<RequireAuth><FirmWorkpaper /></RequireAuth>} />
          <Route path="/onboarding/start" element={<Navigate to="/onboarding" replace />} />
          {/* Legacy wizard route — will be workspace-scoped in M2.4. */}
          <Route path="/onboarding" element={<RequireAuth><UnifiedOnboarding /></RequireAuth>} />
          {/* Workspace-scoped wizard — until M2.4 lands, this is the same
              legacy Onboarding component. Rendered under WorkspaceGate so
              the workspace context exists even before the wizard is
              rewritten to consume it. */}
          <Route
            path="/w/:slug/onboarding"
            element={
              <RequireAuth>
                <WorkspaceGate>
                  <UnifiedOnboarding />
                </WorkspaceGate>
              </RequireAuth>
            }
          />

          {/* ---- New workspace-scoped routes (M1.5) ---- */}
          {/* Kept as flat routes rather than nested Outlet children so
              they mirror the legacy structure exactly — makes it obvious
              during code review that every legacy page has a workspace
              analogue and nothing was dropped. */}
          <Route
            path="/w/:slug/app/dashboard"
            element={workspacePage(<Dashboard />)}
          />
          {/* V98-3-3 / 3-4: Today, composed from the viewer's hats (GET /today). */}
          <Route
            path="/w/:slug/app/today"
            element={workspacePage(<Today />)}
          />
          {/* V98-3-5: program-neutral item page (actions per hat; ?from= return crumb). */}
          <Route
            path="/w/:slug/app/items/:kind/:id"
            element={workspacePage(<ItemPage />)}
          />
          {/* V98-3-1: Programs (sidebar group + mobile tab). */}
          <Route
            path="/w/:slug/app/programs"
            element={workspacePage(<ProgramsIndex />)}
          />
          {/* V98-3-2: ProgramShell — StageRail tabs Scope · Prepare · Audit ·
              Certified[Operate ‖ Report]; no :tab opens the current stage. */}
          <Route
            path="/w/:slug/app/programs/:program"
            element={workspacePage(<ProgramShell />)}
          />
          {/* V1035-2-2: per-control detail page (declared before :tab catches "controls"). */}
          <Route
            path="/w/:slug/app/programs/:program/controls/:requirementId"
            element={workspacePage(<ControlDetail />)}
          />
          {/* V102-8-6: CMMC POA&M view + affirmation flow (declared before :tab catches "poam"). */}
          <Route
            path="/w/:slug/app/programs/:program/poam"
            element={workspacePage(<CmmcPoam />)}
          />
          {/* V98-5-7: the mock audit (internal working document) and management-response authoring. */}
          <Route
            path="/w/:slug/app/programs/:program/operate/mock-audit"
            element={workspacePage(<MockAudit />)}
          />
          {/* V103-10-6 / V103-4-5: the government-cloud move and the single-component replacement (Scope stage). */}
          <Route
            path="/w/:slug/app/programs/:program/scope/move"
            element={workspacePage(<TransitionPage />)}
          />
          <Route
            path="/w/:slug/app/programs/:program/scope/replace/:componentId"
            element={workspacePage(<ReplacePage />)}
          />
          <Route
            path="/w/:slug/app/programs/:program/:tab"
            element={workspacePage(<ProgramShell />)}
          />
          <Route
            path="/w/:slug/app/responses/:id"
            element={workspacePage(<ResponsePage />)}
          />
          {/* V98-3-1/3-2: Boundary (sidebar, under Systems) → a program's Scope tab. */}
          <Route
            path="/w/:slug/app/boundary"
            element={workspacePage(<BoundaryRedirect />)}
          />
          {/* V98-3-1: Policies — published policies + acknowledgments. */}
          <Route
            path="/w/:slug/app/policies"
            element={workspacePage(<PoliciesPage />)}
          />
          <Route
            path="/w/:slug/app/register"
            element={workspacePage(<Register />)}
          />
          {/* V3-9-9 / ADR-0025 · AI System → Components inventory */}
          <Route
            path="/w/:slug/app/systems/:systemId/components"
            element={workspacePage(<ComponentInventory />)}
          />
          <Route
            path="/w/:slug/app/register/citations"
            element={workspacePage(<RegisterCitations />)}
          />
          <Route
            path="/w/:slug/app/register/:id"
            element={workspacePage(<RegisterItemDetail />)}
          />
          <Route
            path="/w/:slug/app/roadmap"
            element={workspacePage(<Roadmap />)}
          />
          <Route
            path="/w/:slug/app/suppliers"
            element={workspacePage(<Suppliers />)}
          />
          <Route
            path="/w/:slug/app/suppliers/:vendorId"
            element={workspacePage(<SupplierDetail />)}
          />
          <Route
            path="/w/:slug/app/attested-capabilities"
            element={workspacePage(<AttestedCapabilities />)}
          />
          <Route
            path="/w/:slug/app/audit/settings"
            element={workspacePage(<AuditSettings />)}
          />
          <Route
            path="/w/:slug/app/audit/events/:eventId"
            element={workspacePage(<AuditEventDetail />)}
          />
          <Route
            path="/w/:slug/app/systems"
            element={workspacePage(<Systems />)}
          />
          {/* V113-7-2 / 7-6: Monitoring (cross-system table, shared feeds) and the configuration item drill-in. */}
          <Route path="/w/:slug/app/monitoring" element={workspacePage(<Monitoring />)} />
          <Route path="/w/:slug/app/monitoring/configuration-items/:itemId" element={workspacePage(<ConfigurationItemPage />)} />
          <Route path="/w/:slug/app/setup" element={workspacePage(<Setup />)} />
          {/* V98-3-2: SOC 2 readiness is rehosted in the SOC 2 program; the
              old route (and its ?filter / ?setup / ?acknowledge deep links)
              lands on the Prepare tab. */}
          <Route path="/w/:slug/app/soc2" element={<WorkspaceRedirect to="programs/soc2/prepare" />} />
          <Route path="/w/:slug/app/systems/new" element={<NewSystemRedirect />} />
          <Route
            path="/w/:slug/app/systems/:systemId"
            element={workspacePage(<SystemDetail />)}
          />
          {/* ADR-0010 · PR-P1-1: /reports is deprecated in favor of
              /deliverables. Keep a workspace-scoped redirect for 12
              months so bookmarks + external doc links resolve to the
              new URL. TODO(2027-08): delete this route after the
              12-month sunset window. Server-side (Worker) 301 is
              deferred — SPA-level redirect is sufficient for the SPA-
              only bookmarks we're bridging. */}
          <Route
            path="/w/:slug/app/reports"
            element={<ReportsToDeliverablesRedirect />}
          />
          <Route
            path="/w/:slug/app/evidence"
            element={workspacePage(<Evidence />)}
          />
          {/* UC-7 Audit trail — PR-Cluster-7-a. Backend routes live
              (Phase D); this page renders the read-only event window +
              chain-verify affordance. Detail-modal / audit-package
              generator / snapshot mismatch banner deferred. */}
          <Route
            path="/w/:slug/app/audit"
            element={workspacePage(<AuditLog />)}
          />
          <Route
            path="/w/:slug/app/queue"
            element={workspacePage(<Queue />)}
          />
          {/* V104-G-10: author a rule by hand for a Needs authoring item. */}
          <Route
            path="/w/:slug/app/queue/authoring/:itemId"
            element={workspacePage(<AuthorRule />)}
          />
          <Route
            path="/w/:slug/app/sources"
            element={workspacePage(<Sources />)}
          />
          <Route
            path="/w/:slug/app/questionnaires"
            element={workspacePage(<Questionnaires />)}
          />
          <Route
            path="/w/:slug/app/questionnaires/new"
            element={workspacePage(<QuestionnaireNew />)}
          />
          <Route
            path="/w/:slug/app/questionnaires/:id"
            element={workspacePage(<QuestionnaireDetail />)}
          />
          {/* Team & assignments — standalone (not inside Settings layout) */}
          <Route
            path="/w/:slug/app/team"
            element={workspacePage(<TeamSettings />)}
          />
          {/* Settings sub-navigation: SettingsLayout renders a left
              sub-nav + <Outlet /> for the active settings page. The
              parent route carries the workspace chrome (RequireAuth +
              WorkspaceGate + AppShell); children render inside Outlet. */}
          <Route
            path="/w/:slug/app/settings"
            element={workspacePage(<SettingsLayout />)}
          >
            {/* Default redirect: bare /settings → /settings/profile */}
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<ProfileSettings />} />
            {/* Team moved to standalone /w/:slug/app/team */}
            <Route path="team" element={<Navigate to="../../team" relative="path" replace />} />
            <Route path="billing" element={<Billing />} />
            <Route path="sso" element={<SsoSettings />} />
            <Route path="compliance" element={<ComplianceSettings />} />
            {/* v9.2 / V92-6-2: coverage-region selector (D-92-3) */}
            <Route path="coverage-regions" element={<CoverageRegionSettings />} />
            {/* PR-Cluster-7-b: webhook admin (audit-event stream) */}
            <Route path="webhooks" element={<WebhookSettings />} />
            {/* V114-5-2 / D-114-11: outbound EVENT webhooks (finding + pack-validity; routes/webhooks.ts) */}
            <Route path="event-webhooks" element={<EventWebhookSettings />} />
            {/* PR-Cluster-6-a: assessor engagements */}
            <Route path="assessors" element={<AssessorEngagements />} />
            {/* V98-6 / ADR-0047: invite an audit FIRM (the firm picks its people). */}
            <Route path="audit-firms" element={<AuditFirmEngagements />} />
            {/* PR-Cluster-7-d: workspace API tokens */}
            <Route path="api" element={<ApiTokenSettings />} />
            {/* PR-Cluster-6-i: workspace-wide dispute inbox */}
            <Route path="disputes" element={<DisputesInbox />} />
            {/* ADR-0012 · PR-P1-2: Integrations + Security */}
            <Route path="integrations" element={<Navigate to="../../setup" relative="path" replace />} />
            <Route path="security" element={<Security />} />
            <Route path="signatures" element={<SignatureRoutingSettings />} />
            <Route path="signing" element={<SigningSettings />} />
            <Route path="control-specs" element={<ControlSpecSettings />} />
          </Route>
          {/* PR-Cluster-6-l: assessor engagement workpaper — print-
              first summary of every verdict + dispute + Q&A +
              evidence request on this engagement. Deep-linked
              from AssessorInbox after picking a workspace. */}
          {/* V98-6 / ADR-0047: the client's side of a firm engagement (+ ?preview=auditor). */}
          <Route path="/w/:slug/app/audit-firms/:engagementId" element={workspacePage(<FirmClientView />)} />
          <Route
            path="/w/:slug/app/assessor/workpaper/:engagementId"
            element={workspacePage(<AssessorWorkpaper />)}
          />
          {/* V98-3-1: assessor nav "Workpapers" (was a dead target) →
              the engagement in this session's scope bar. */}
          <Route
            path="/w/:slug/app/assessor/workpaper"
            element={<RequireAuth><WorkspaceGate><AssessorWorkpaperIndex /></WorkspaceGate></RequireAuth>}
          />
          {/* PR-Cluster-6-d: supplier queue — evidence requests routed
              to me across every control I own. Marcus/Nadia landing
              when the 3PAO is mid-engagement. */}
          <Route
            path="/w/:slug/app/evidence-requests"
            element={workspacePage(<MyEvidenceRequests />)}
          />

          {/* ADR-0009 · PR-P1-3b: register items assigned to the
              current member, ordered by priority. */}
          <Route
            path="/w/:slug/app/mine"
            element={workspacePage(<MyWork />)}
          />
          {/* PR-P3-2 · UC-4 Alerts inbox. Preferences before :id so the
              literal path wins (React Router v6 ranks static routes
              over dynamic ones regardless of declaration order, but
              this keeps the file readable). */}
          <Route
            path="/w/:slug/app/alerts"
            element={workspacePage(<AlertsInbox />)}
          />
          <Route
            path="/w/:slug/app/alerts/preferences"
            element={workspacePage(<AlertPreferences />)}
          />
          <Route
            path="/w/:slug/app/alerts/:id"
            element={workspacePage(<AlertDetail />)}
          />
          {/* ADR-0012 · PR-P1-2: Integrations + Security moved into the
              nested settings route above (SettingsLayout). */}
          {/* ADR-0010 · PR-P1-1: /deliverables is the canonical route.
              Full page rewrite (server-generated packages list,
              audit-package tier, jsPDF removal) lands in Phase 9;
              until then this shows the interim Reports/Deliverables
              hybrid page. */}
          <Route
            path="/w/:slug/app/deliverables"
            element={workspacePage(<Reports />)}
          />
          {/* V6-6-5: Lifecycle stage landing pages */}
          <Route
            path="/w/:slug/app/plan"
            element={workspacePage(<PlanStage />)}
          />
          <Route
            path="/w/:slug/app/build"
            element={workspacePage(<BuildStage />)}
          />
          <Route
            path="/w/:slug/app/release"
            element={workspacePage(<ReleaseStage />)}
          />
          <Route
            path="/w/:slug/app/operate"
            element={workspacePage(<OperateStage />)}
          />
          <Route
            path="/w/:slug/app/sell"
            element={workspacePage(<SellStage />)}
          />
          <Route
            path="/w/:slug/app/use-cases"
            element={workspacePage(<UseCaseIntake />)}
          />
          <Route
            path="/w/:slug/app/blueprint"
            element={workspacePage(<Blueprint />)}
          />
          <Route
            path="/w/:slug/app/common-controls"
            element={workspacePage(<CommonControls />)}
          />
          {/* V102-8-7: test library (sibling of Common Controls). */}
          <Route
            path="/w/:slug/app/library/tests"
            element={workspacePage(<TestLibrary />)}
          />
          <Route
            path="/w/:slug/app/discovered-tools"
            element={workspacePage(<DiscoveredTools />)}
          />
          {/* V98-5-6: Calendar & trust center (the nav's top-level target). */}
          <Route
            path="/w/:slug/app/calendar"
            element={workspacePage(<CalendarTrust />)}
          />
          <Route
            path="/w/:slug/app/exceptions"
            element={workspacePage(<Exceptions />)}
          />
          <Route
            path="/w/:slug/app/incidents"
            element={workspacePage(<IncidentIntake />)}
          />
          <Route
            path="/w/:slug/app/incidents/:incidentId"
            element={workspacePage(<IncidentDetail />)}
          />
          <Route
            path="/w/:slug/app/insurability"
            element={workspacePage(<InsurabilityPack />)}
          />
          <Route
            path="/w/:slug/app/findings"
            element={workspacePage(<Findings />)}
          />
          <Route
            path="/w/:slug/app/releases"
            element={workspacePage(<ReleaseManager />)}
          />
          <Route
            path="/w/:slug/app/suppliers/:id/register"
            element={workspacePage(<SupplierRegister />)}
          />
          {/* MoreScreen deleted (NAV-2-6); redirect preserves bookmarks
              until Phase 3 replaces the mobile tabbar entirely. */}
          <Route
            path="/w/:slug/app/more"
            element={<Navigate to="../today" relative="path" replace />}
          />

          {/* Organization-tier pages (ADR-0013 / PR-P10-4 / NAV-4-1) */}
          <Route path="/o/:orgSlug" element={<RequireAuth><OrgShell /></RequireAuth>}>
            <Route path="dashboard" element={<OrgDashboard />} />
            <Route path="settings" element={<OrgSettings />} />
            <Route path="ai-systems" element={<OrgAiSystems />} />
            <Route path="suppliers" element={<OrgSupplierPortfolio />} />
            <Route path="suppliers/:id" element={<OrgSupplierDetail />} />
            <Route path="findings" element={<OrgFindings />} />
            <Route path="incidents" element={<OrgIncidents />} />
            <Route path="alerts" element={<OrgAlerts />} />
            <Route path="vendor-attestations" element={<OrgVendorAttestations />} />
            <Route path="disclosure-requests" element={<OrgDisclosureRequests />} />
          </Route>

          {/* Invitation acceptance — NOT gated by RequireAuth so the
              page can render its own "sign in first" state without
              losing the token to a redirect. */}
          <Route path="/invite/accept" element={<InviteAccept />} />


          {/* ---- Ultra-legacy top-level shortcuts ----
             Every literal below intentionally targets /app/* so the
             LegacyAppRedirect wrapper (mounted at /app/*) can resolve
             the user's default_workspace_id AND slug-scope in one
             place, rather than duplicating that logic here per-route.
             scope-check-ignore per line — these are the sanctioned
             one-hop chain to the workspace-scoped destination. */}
          <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace /> /* scope-check-ignore */} />
          <Route path="/register" element={<Navigate to="/app/register" replace /> /* scope-check-ignore */} />
          <Route path="/register/:id" element={<Navigate to="/app/register" replace /> /* scope-check-ignore */} />
          <Route path="/roadmap" element={<Navigate to="/app/roadmap" replace /> /* scope-check-ignore */} />
          <Route path="/systems" element={<Navigate to="/app/systems" replace /> /* scope-check-ignore */} />
          {/* ADR-0010 · PR-P1-1: legacy top-level shortcut now points
              directly at /app/deliverables (the LegacyAppRedirect
              wrapper then resolves default_workspace slug). Skips the
              intermediate /w/:slug/app/reports hop that the workspace-
              scoped redirect above provides for bookmarks. */}
          <Route path="/reports" element={<Navigate to="/app/deliverables" replace /> /* scope-check-ignore */} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </AppQueryProvider>
    </AuthProvider>
  );
}

